import React from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { BookText, Languages } from 'lucide-react';
import useTranslate from '../../hooks/useTranslate';

/** "Translated by AI · Show original" note for auto-translated excerpts. */
const TranslatedNote = ({ loading, showOriginal, setShowOriginal, testid }) => (
  <p className="flex items-center gap-1.5 text-[10px] text-slate-500 dark:text-slate-400 mt-2" data-testid={testid}>
    <Languages className="w-3 h-3" />
    {loading ? 'Traduciendo…' : 'Traducido por IA'}
    <span>·</span>
    <button
      type="button"
      className="underline hover:text-slate-700 dark:hover:text-slate-200"
      onClick={() => setShowOriginal((v) => !v)}
      data-testid={`${testid}-toggle`}
    >
      {showOriginal ? 'Ver traducción' : 'Ver original'}
    </button>
  </p>
);

/** Matthew Henry + Verse Lens public-domain excerpts, auto-translated in Spanish. */
const CommentaryExcerpts = ({ language, mhExcerpt, verseLens, slot }) => {
  const sources = verseLens?.sources || [];
  const texts = slot === 'mh'
    ? [mhExcerpt?.text || '']
    : sources.map((s) => (s.key === 'tsk' ? '' : s.snippet || ''));
  const tr = useTranslate(texts, language);
  const es = language === 'es';

  if (slot === 'mh') {
    if (!mhExcerpt?.text) return null;
    return (
      <Card className="border-l-4 border-amber-600 bg-amber-50/40 dark:bg-amber-950/20 shadow-md" data-testid="companion-mh-excerpt">
        <CardHeader className="pb-2">
          <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-2">
            <BookText className="w-4 h-4" />
            Matthew Henry · {mhExcerpt.reference}
          </h3>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed italic" data-testid="companion-mh-text">
            {tr.output[0]}
          </p>
          <p className="text-[10px] text-amber-700/70 dark:text-amber-300/70 italic mt-2">
            {es ? 'Dominio público · Edición concisa' : 'Public Domain · Concise edition'}
          </p>
          {tr.isTranslated && <TranslatedNote {...tr} testid="companion-mh-translated" />}
        </CardContent>
      </Card>
    );
  }

  if (!sources.length) return null;
  return (
    <Card className="border-l-4 border-teal-600 bg-teal-50/40 dark:bg-teal-950/20 shadow-md" data-testid="companion-verse-lens">
      <CardHeader className="pb-2">
        <h3 className="text-sm font-semibold text-teal-800 dark:text-teal-200 flex items-center gap-2">
          <BookText className="w-4 h-4" />
          {es ? 'Lente del Versículo' : 'Verse Lens'} · {verseLens.available} {es ? 'fuentes' : 'sources'}
        </h3>
      </CardHeader>
      <CardContent className="space-y-2">
        {sources.map((src, i) => (
          <div key={src.key} data-testid={`companion-vlens-${src.key}`} className="text-xs">
            <span className="font-bold text-teal-700 dark:text-teal-300 mr-1">{src.label}:</span>
            <span className="text-slate-700 dark:text-slate-300 italic">
              {src.key === 'tsk' ? src.snippet : tr.output[i]}
            </span>
          </div>
        ))}
        {tr.isTranslated && <TranslatedNote {...tr} testid="companion-vlens-translated" />}
      </CardContent>
    </Card>
  );
};

export default CommentaryExcerpts;
